import { useEffect, useMemo } from "react";
import usePersistedState from "@/lib/usePersistedState";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function monthStart(y, m) { return iso(new Date(y, m, 1)); }
function monthEnd(y, m) { return iso(new Date(y, m + 1, 0)); }

// Indian financial year (April–March)
function fyForDate(d) {
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return { from: `${y}-04-01`, to: `${y + 1}-03-31`, label: `FY ${y}-${String(y + 1).slice(-2)}` };
}

export function presetRange(preset) {
  const now = new Date();
  if (preset === "this-month") {
    return { from: monthStart(now.getFullYear(), now.getMonth()), to: monthEnd(now.getFullYear(), now.getMonth()) };
  }
  if (preset === "last-month") {
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return { from: monthStart(d.getFullYear(), d.getMonth()), to: monthEnd(d.getFullYear(), d.getMonth()) };
  }
  if (preset === "last-3") {
    const start = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    return { from: monthStart(start.getFullYear(), start.getMonth()), to: monthEnd(now.getFullYear(), now.getMonth()) };
  }
  if (preset === "this-fy") return fyForDate(now);
  if (preset === "last-fy") {
    const prev = new Date(now.getFullYear() - 1, now.getMonth(), 1);
    return fyForDate(prev);
  }
  if (preset === "all") return { from: "2000-01-01", to: "2999-12-31" };
  return null;
}

const MONTH_LABEL = (m) =>
  ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][m];

/**
 * PeriodPicker — month-wise default + presets + custom date range.
 * Props:
 *   value:   { from, to }
 *   onChange: ({from, to, preset}) => void
 *   defaultPreset: "this-month" | "last-month" | "last-3" | "this-fy" | "last-fy" | "all" | "custom"
 *   compact: render as single inline row
 */
export default function PeriodPicker({
  value,
  onChange,
  defaultPreset = "this-month",
  testIdPrefix = "period",
  compact = false,
  showAll = true,
}) {
  // preset / month / year survive navigation (per page, via testIdPrefix)
  const [preset, setPreset] = usePersistedState(`pp:${testIdPrefix}:preset`, defaultPreset);
  const initial = value?.from ? new Date(value.from) : new Date();
  const [year, setYear] = usePersistedState(`pp:${testIdPrefix}:year`, initial.getFullYear());
  const [month, setMonth] = usePersistedState(`pp:${testIdPrefix}:month`, initial.getMonth());

  const years = useMemo(() => {
    const ny = new Date().getFullYear();
    const out = [];
    for (let y = ny + 1; y >= ny - 5; y--) out.push(y);
    return out;
  }, []);

  // Push range up whenever preset/month/year/custom-input changes
  useEffect(() => {
    if (preset === "custom") return; // custom is driven by inputs only
    if (preset === "month") {
      onChange?.({ from: monthStart(year, month), to: monthEnd(year, month), preset });
    } else {
      const r = presetRange(preset);
      if (r) onChange?.({ ...r, preset });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, year, month]);

  return (
    <div className={`flex ${compact ? "flex-row items-end" : "flex-col md:flex-row md:items-end"} gap-3 flex-wrap`} data-testid={`${testIdPrefix}-picker`}>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">Period</Label>
        <Select value={preset} onValueChange={(v) => setPreset(v)}>
          <SelectTrigger className="mt-1.5 h-10 rounded-sm bg-white w-44" data-testid={`${testIdPrefix}-preset`}>
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 text-brand-muted" />
              <SelectValue />
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="this-month">This Month</SelectItem>
            <SelectItem value="last-month">Last Month</SelectItem>
            <SelectItem value="last-3">Last 3 Months</SelectItem>
            <SelectItem value="month">Pick Month…</SelectItem>
            <SelectItem value="this-fy">This Financial Year</SelectItem>
            <SelectItem value="last-fy">Last Financial Year</SelectItem>
            {showAll && <SelectItem value="all">All Time</SelectItem>}
            <SelectItem value="custom">Custom Dates</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {preset === "month" && (
        <>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">Month</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="mt-1.5 h-10 rounded-sm bg-white w-32" data-testid={`${testIdPrefix}-month`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[...Array(12).keys()].map((m) => <SelectItem key={m} value={String(m)}>{MONTH_LABEL(m)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">Year</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="mt-1.5 h-10 rounded-sm bg-white w-28" data-testid={`${testIdPrefix}-year`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {preset === "custom" && (
        <>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">From</Label>
            <Input type="date" value={value?.from || ""}
              onChange={(e) => onChange?.({ from: e.target.value, to: value?.to || "", preset })}
              data-testid={`${testIdPrefix}-from`}
              className="mt-1.5 h-10 rounded-sm bg-white" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">To</Label>
            <Input type="date" value={value?.to || ""}
              onChange={(e) => onChange?.({ from: value?.from || "", to: e.target.value, preset })}
              data-testid={`${testIdPrefix}-to`}
              className="mt-1.5 h-10 rounded-sm bg-white" />
          </div>
        </>
      )}

      {preset !== "custom" && (
        <div className="text-[11px] text-brand-muted self-end pb-2.5" data-testid={`${testIdPrefix}-range-label`}>
          {value?.from} → {value?.to}
        </div>
      )}
    </div>
  );
}
